import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, TrendingUp, Lock, ArrowUpRight } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

function ProductPreview() {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <div className="absolute -inset-4 rounded-3xl bg-white/[0.03] blur-2xl" />
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] p-5 shadow-2xl backdrop-blur-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-md bg-white">
              <BrandLogo className="h-full w-full" />
            </span>
            <span className="text-[13px] font-semibold text-white">{t("authShell.previewOverview")}</span>
          </div>
          <span className="rounded-full bg-emerald-400/15 px-2 py-0.5 text-[10px] font-medium text-emerald-300">{t("authShell.previewLive")}</span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          {[
            { l: t("authShell.previewRevenue"), v: "84 200 kr", t: "+18%" },
            { l: t("authShell.previewOpenCases"), v: "7", t: "-3" },
          ].map((s) => (
            <div key={s.l} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
              <p className="text-[10px] uppercase tracking-[0.12em] text-white/50">{s.l}</p>
              <p className="mt-1 font-mono text-base font-semibold text-white">{s.v}</p>
              <p className="mt-0.5 flex items-center gap-0.5 text-[10px] font-medium text-emerald-300">
                <TrendingUp className="h-3 w-3" /> {s.t}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-end gap-1.5 rounded-xl border border-white/10 bg-white/[0.03] p-3">
          {[38, 52, 34, 66, 48, 72, 58, 84, 62, 90].map((h, i) => (
            <span key={i} className="flex-1 rounded-sm bg-gradient-to-t from-white/20 to-white/60" style={{ height: `${h}px` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function AuthShell({ children, title, subtitle }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <div className="relative hidden w-[52%] overflow-hidden bg-[#08080B] text-white lg:flex">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-24 -top-24 h-[420px] w-[420px] rounded-full bg-primary/25 blur-[120px]" />
          <div className="absolute -bottom-32 right-0 h-[380px] w-[380px] rounded-full bg-indigo-500/15 blur-[120px]" />
          <div className="absolute inset-0 opacity-[0.05] [background-image:radial-gradient(currentColor_1px,transparent_1px)] [background-size:22px_22px]" />
        </div>
        <div className="relative z-10 flex h-full w-full flex-col justify-between p-12 xl:p-16">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-9 w-9 overflow-hidden rounded-xl">
              <BrandLogo className="h-full w-full" />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">ArcQuan Platform</span>
          </Link>
          <div className="max-w-md">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-medium text-white/70 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              {t("authShell.badge")}
            </div>
            <h2 className="font-display text-[42px] font-black leading-[1.03] tracking-tight xl:text-5xl">
              {t("authShell.titleLine1")}
              <br />
              <span className="text-white/55">{t("authShell.titleLine2")}</span>
            </h2>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/60">{t("authShell.subtitle")}</p>
            <div className="mt-9"><ProductPreview /></div>
          </div>
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-5 text-[11px] text-white/40">
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> {t("authShell.secureMultiTenant")}</span>
              <span className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> {t("authShell.encryptedData")}</span>
              <span>© 2026 ArcQuan</span>
            </div>
          </div>
        </div>
      </div>

      <div className="relative flex w-full items-center justify-center overflow-hidden px-6 py-12 lg:w-[48%]">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-1/4 h-72 w-72 rounded-full bg-primary/10 blur-[110px]" />
          <div className="absolute bottom-0 right-1/4 h-72 w-72 rounded-full bg-[#7C3AED]/10 blur-[120px]" />
          <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] [background-image:radial-gradient(currentColor_1px,transparent_1px)] [background-size:22px_22px]" />
          <div className="lp-streak absolute left-[28%] top-0 h-full w-px bg-gradient-to-b from-transparent via-primary/15 to-transparent" />
          <div className="lp-streak absolute right-[26%] top-0 h-full w-px bg-gradient-to-b from-transparent via-[#7C3AED]/15 to-transparent" style={{ animationDelay: "2.5s" }} />
          {[...Array(6)].map((_, i) => (
            <span key={i} className="lp-particle" style={{ left: `${15 + i * 13}%`, top: `${(i * 29) % 78 + 8}%`, animationDelay: `${i * 0.8}s` }} />
          ))}
        </div>
        <div className="absolute right-6 top-6 z-20 flex items-center gap-3">
          <LanguageSwitcher variant="light" />
          <Link to="/" className="hidden items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground lg:flex" data-testid="auth-home-link">
            {t("common.backHome")} <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="relative z-10 w-full max-w-sm animate-in fade-in slide-in-from-bottom-3 duration-500">
          <div className="mb-8 flex flex-col items-center text-center">
            <Link to="/" className="mb-6 flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl ring-1 ring-border" data-testid="auth-logo">
              <BrandLogo className="h-full w-full" />
            </Link>
            <h1 className="font-display text-[26px] font-bold leading-tight tracking-tight sm:text-[32px]">{title}</h1>
            {subtitle && <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>}
          </div>
          {children}
          <div className="mt-8 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground/70">
            <Lock className="h-3 w-3" />
            {t("common.secureLogin")}
          </div>
        </div>
      </div>
    </div>
  );
}
